import React, { FC, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Global } from './constants';
import { ConfigProvider } from 'antd';

import NotFound from './pages/404.page';
import SuspenseWrapper from './hoc/suspense.hoc';
import ProtectedRoute from './hoc/protected-routes.hoc';
import AuthLayout from './components/layouts/auth-layout.component';
import AdminLayout from './components/layouts/admin-layout.component';
import CustomLayout from './components/layouts/custom-layout.component';
import { antdTheme } from './theme/tokens';

import './scss/main.sass';

type Props = {
  isLoggedIn?: any,
}

const App: FC<Props> = ({ isLoggedIn }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const HomePage = React.lazy(() => import('./pages/admin/home.page'));
  const ProfilePage = React.lazy(() => import('./pages/admin/profile.page'));
  const CompaniesPage = React.lazy(() => import('./pages/admin/branchs.page'));
  const AdminsPage = React.lazy(() => import('./pages/admin/admins.page'));
  const TemplatesPage = React.lazy(() => import('./pages/admin/templates.page'));
  const LoginPage = React.lazy(() => import('./pages/login.page'));
  const DocumentsPage = React.lazy(() => import('./pages/admin/documents.page'));
  const ReportsPage = React.lazy(() => import('./pages/admin/reports.page'));
  const DocumentCreatorPage = React.lazy(() => import('./pages/admin/document-creator.page'));
  const DocumentCreatorPageV2 = React.lazy(() => import('./pages/admin/document-creator-v2.page'));
  const DocumentWizardPage = React.lazy(() => import('./pages/admin/document-wizard.page'));
  const SubscriptionPage = React.lazy(() => import('./pages/admin/subscription.page'));
  const AiCreatorPage = React.lazy(() => import('./pages/admin/ai-creator.page'));
  const TemplateEditorPage = React.lazy(() => import('./pages/admin/template-editor.page'));
  const VoboPage = React.lazy(() => import('./pages/vobo.page'));
  const VoboCompletedPage = React.lazy(() => import('./pages/completed.page'));
  const DocumentDetail = React.lazy(() => import('./pages/admin/document-detail.page'));
  const ResetPasswordPage = React.lazy(() => import('./pages/reset-password.page'));
  const ForgotPasswordPage = React.lazy(() => import('./pages/forgot-password.page'));
  const GenerateTokenPage = React.lazy(() => import('./pages/generate-token.page'));

  useEffect(() => {
    const unprotectedRoutes = Global.UNPROTECTED_ROUTES;
    const path = window.location.pathname;

    const isUnprotected = unprotectedRoutes.some(route => path.startsWith(route));

    if (isUnprotected) {
      return;
    }

    if (!isLoggedIn) {
      navigate(Global.APP_URLS.LOGIN);
    }
  }, [isLoggedIn, navigate]);

  return (
    <ConfigProvider theme={antdTheme}>
      <SuspenseWrapper>
        <Routes>
          <Route path="/" element={
            <ProtectedRoute route={location.pathname}>
              <AuthLayout />
            </ProtectedRoute>
          }>
            <Route index element={<LoginPage />} />
          </Route>
          <Route path="/auth" element={<AuthLayout />}>
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="forgot" element={<ForgotPasswordPage />} />
            <Route path="generateToken" element={<GenerateTokenPage />} />
          </Route>
          <Route path="/admin" element={
            <ProtectedRoute route={location.pathname}>
              <AdminLayout />
            </ProtectedRoute>
          }>
            {/* Inicio sustituye al listado como pantalla de arranque (PRD 5.1). */}
            <Route index element={<HomePage />} />
            <Route path="/admin/inicio" element={<HomePage />} />
            <Route path="/admin/profile" element={<ProfilePage />} />
            <Route path="/admin/companies" element={<CompaniesPage />} />
            <Route path="/admin/admins" element={<AdminsPage />} />
            <Route path="/admin/templates" element={<TemplatesPage />} />
            <Route path="/admin/templates/editor/:templateId" element={<TemplateEditorPage />} />
            <Route path="/admin/documents" element={<DocumentsPage />} />
            <Route path="/admin/document/creator" element={<DocumentCreatorPage />} />
            <Route path="/admin/document/creator-v2" element={<DocumentCreatorPageV2 />} />
            {/* Asistente unificado. Las rutas de los dos creadores siguen
                activas hasta que este quede validado en producción. */}
            <Route path="/admin/document/nuevo" element={<DocumentWizardPage />} />
            <Route path="/admin/document/crear-con-ia" element={<AiCreatorPage />} />
            <Route path="/admin/suscripcion" element={<SubscriptionPage />} />
            <Route path="/admin/document/:documentId" element={<DocumentDetail />} />
            <Route path="/admin/reports" element={<ReportsPage />} />
          </Route>
          <Route path="/document" element={<CustomLayout />}>
            <Route path="/document/vobo/:documentId" element={<VoboPage />} />
            <Route path="/document/complete-document/:documentId" element={<VoboCompletedPage />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SuspenseWrapper>
    </ConfigProvider>
  );
};

const mapStateToProps = (state: any) => ({
  isLoggedIn: state.auth?.login || null,
});

export default connect(mapStateToProps, null)(App);
